import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUtmTaxonomyStore } from '@/store/utm-taxonomy-store';
import UtmColorPicker from '@/components/utm/shared/UtmColorPicker';
import UtmIconPicker from '@/components/utm/shared/UtmIconPicker';
import { Save, X } from 'lucide-react';

const UtmGroupForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { categories, groups, createGroup, updateGroup, fetchTaxonomy } =
    useUtmTaxonomyStore();

  const isEditMode = Boolean(id);
  const existingGroup = groups.find((g) => g.id === id);

  const [formData, setFormData] = useState({
    categoryId: '',
    code: '',
    name: '',
    defaultVisibleStatus: 'VISIBLE_WHEN_CHECKED' as 'ALWAYS_VISIBLE' | 'VISIBLE_WHEN_CHECKED',
    description: '',
    iconType: 'BUILTIN' as 'BUILTIN' | 'SVG' | 'IMAGE_URL' | 'S3_OBJECT',
    iconValue: 'map-pin',
    iconColor: '#ffffff',
    iconBgColor: '#1e293b',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchTaxonomy();
  }, [fetchTaxonomy]);

  useEffect(() => {
    if (isEditMode && existingGroup) {
      setFormData({
        categoryId: existingGroup.categoryId,
        code: existingGroup.code,
        name: existingGroup.name,
        defaultVisibleStatus: existingGroup.defaultVisibleStatus || 'VISIBLE_WHEN_CHECKED',
        description: existingGroup.description || '',
        iconType: existingGroup.iconType,
        iconValue: existingGroup.iconValue,
        iconColor: existingGroup.iconColor || '#ffffff',
        iconBgColor: existingGroup.iconBgColor || '#1e293b',
      });
    }
  }, [isEditMode, existingGroup]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (isEditMode && id) {
        await updateGroup(id, formData);
      } else {
        await createGroup(formData);
      }
      navigate('/admin/uzamaps/groups');
    } catch (error) {
      console.error('Failed to save group:', error);
      alert('Failed to save group. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIconChange = (iconType: string, iconValue: string) => {
    setFormData((prev) => ({
      ...prev,
      iconType: iconType as 'BUILTIN' | 'SVG' | 'IMAGE_URL' | 'S3_OBJECT',
      iconValue,
    }));
  };

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
          {isEditMode ? 'Edit Group' : 'Create Group'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {isEditMode ? 'Update group details' : 'Add a new group within a UTM category'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Basic Information</h2>

          <div className="space-y-4">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.categoryId}
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:border-ring"
                required
                disabled={isEditMode}
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {isEditMode && (
                <p className="text-xs text-muted-foreground mt-1">
                  Category cannot be changed after creation
                </p>
              )}
            </div>

            {/* Code */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-ring"
                placeholder="e.g., nfz-military"
                required
                disabled={isEditMode}
              />
              {isEditMode && (
                <p className="text-xs text-muted-foreground mt-1">
                  Code cannot be changed after creation
                </p>
              )}
              {!isEditMode && (
                <p className="text-xs text-muted-foreground mt-1">Unique identifier used in URLs</p>
              )}
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-ring"
                placeholder="e.g., Military No-Fly Zones"
                required
              />
            </div>

            {/* Default visible status */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Default visible status
              </label>
              <select
                value={formData.defaultVisibleStatus}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    defaultVisibleStatus: e.target.value as 'ALWAYS_VISIBLE' | 'VISIBLE_WHEN_CHECKED',
                  })
                }
                className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:border-ring"
              >
                <option value="ALWAYS_VISIBLE">Always visible</option>
                <option value="VISIBLE_WHEN_CHECKED">Visible when checked</option>
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-ring"
                placeholder="Optional description"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Icon Settings */}
        <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Icon Settings</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Icon</label>
              <UtmIconPicker
                iconType={formData.iconType}
                iconValue={formData.iconValue}
                onChange={handleIconChange}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Icon Color
                </label>
                <UtmColorPicker
                  color={formData.iconColor}
                  onChange={(color) => setFormData({ ...formData, iconColor: color })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Background Color
                </label>
                <UtmColorPicker
                  color={formData.iconBgColor}
                  onChange={(color) => setFormData({ ...formData, iconBgColor: color })}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end sm:space-x-0">
          <button
            type="button"
            onClick={() => navigate('/admin/uzamaps/groups')}
            className="flex w-full items-center justify-center space-x-2 rounded-lg bg-muted px-4 py-2 text-foreground transition-colors hover:bg-accent sm:w-auto"
          >
            <X size={18} />
            <span>Cancel</span>
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !formData.name || !formData.code || !formData.categoryId}
            className="flex w-full items-center justify-center space-x-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted sm:w-auto"
          >
            <Save size={18} />
            <span>{isSubmitting ? 'Saving...' : 'Save Group'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default UtmGroupForm;
